import { ArrowLeftOutlined, FilePdfFilled, LoadingOutlined } from "@ant-design/icons";
import { Button, Card, Empty, List, message, Table, Tabs, Tag, Tooltip } from "antd";
import React, { Fragment, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChooseOrView, COMPARISON_COLORS, ComparisonSlot, CurrentOrCompare } from ".";
import { SessionContext } from "../../App";
import PositionsTag from "../../components/PositionsTag";
import SurveyResponseStatusTag from "../../components/SurveyResponseStatusTag";
import {
  GetReportRequest,
  GetReportRequestLayout,
  GetSurveyCampaignBasicInfoResponse,
  GetSurveyCampaignGroupReportResponse,
  GetSurveyCampaignsResponse,
  GetSurveyResponseBySurveyCampaignIdRequest,
} from "../../constants/apiRequestResponse";
import TEST_IDS from "../../constants/cypressTestIds";
import { COLORS } from "../../constants/misc";
import { FeatureFlagStatus, FeatureFlagTemplateKey, SurveyResponse } from "../../constants/types";
import { getReport, getSurveyCampaignBasicInfo, getSurveyCampaigns } from "../../services/api";
import { FeedbackGroupQuestionReportKeys } from "../../services/i8tn/FeedbackReportView/keys";
import { getQueryParam, parseDate, parseTime, setQueryParam } from "../../util/index";

interface Prop {
  report: GetSurveyCampaignGroupReportResponse;
}

export default function FeedbackIndividualView(props: Prop) {
  const context = useContext(SessionContext);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [
    getSurveyCampaignBasicInfoResponse,
    setGetSurveyCampaignBasicInfoResponse,
  ] = useState<GetSurveyCampaignBasicInfoResponse | undefined>();
  const [surveyResponseId, setSurveyResponseId] = useState(
    getQueryParam("surveyResponseId") || ""
  );
  const [comparisonReport, setComparisonReport] = useState("");
  const [report, setReport] = useState("");
  const [isPrintAsPDFLoading, setIsPrintAsPDFLoading] = useState(false);
  const [comparedTo, setComparedTo] = useState<string | undefined>(getQueryParam("individual.comparedTo") || undefined);
  const [isComparisonLoading, setIsComparisonLoading] = useState(false);
  const [
    getSurveyCampaignsResponse,
    setGetSurveyCampaignsResponse,
  ] = useState<GetSurveyCampaignsResponse | undefined>();
  const { t } = useTranslation();
  const currentCampaignId = getQueryParam("surveyCampaignId") || "";
  const [chooseOrView, setChooseOrView] = useState<ChooseOrView | undefined>(getQueryParam("individual.chooseOrView") as ChooseOrView || undefined)
  const [currentOrCompare, setCurrentOrCompare] = useState<CurrentOrCompare | undefined>(getQueryParam('individual.currentOrCompare') as CurrentOrCompare || undefined)

  useEffect(() => {
    setIsLoading(true);
    const req: GetSurveyResponseBySurveyCampaignIdRequest = {
      surveyCampaignId: currentCampaignId,
    };
    getSurveyCampaignBasicInfo(req)
      .then((res) => {
        if (!res.success) throw new Error(res.message);
        setGetSurveyCampaignBasicInfoResponse(res);
      })
      .catch((err) => {
        message.error(err);
        setIsError(true);
      })
      .finally(() => setIsLoading(false));
  }, [currentCampaignId]);

  useEffect(() => {
    // populate the list of survey campaigns we can compare with

    const participant = getSurveyCampaignBasicInfoResponse?.surveyResponsesByParticipant?.find(
      (sr) => sr.participant._id === surveyResponseId
    )?.participant

    if (!participant) return

    getSurveyCampaigns({
      surveyTemplateId: props.report?.surveyCampaign?.surveyTemplateId,
      createdBefore: props.report?.surveyCampaign?.createdAt,
      email: participant.surveyEmployee.email,
    })
      .then((res) => {
        if (!res.success) throw new Error(res.message);
        setGetSurveyCampaignsResponse(res);
      })
      .catch((err) => {
        message.error(err.message)
      });
  }, [surveyResponseId, getSurveyCampaignBasicInfoResponse, props.report?.surveyCampaign])

  // handle current report
  useEffect(() => {
    setIsComparisonLoading(true);
    const req: GetReportRequest = {
      surveyCampaignId: currentCampaignId,
      format: "html",
      layout: GetReportRequestLayout.Individual,
      surveyResponseId: surveyResponseId,
    };
    getReport(req)
      .then((res) => {
        setReport(res?.data);
      })
      .catch((err) => {
        setReport(err.message);
      })
      .finally(() => setIsComparisonLoading(false));
  }, [currentCampaignId, surveyResponseId]);

  // handle comparison report
  useEffect(() => {
    if (!comparedTo) return;

    setIsComparisonLoading(true);
    const req: GetReportRequest = {
      surveyCampaignId: currentCampaignId,
      format: "html",
      layout: GetReportRequestLayout.Individual,
      comparedTo: comparedTo,
      surveyResponseId: surveyResponseId,
    };
    getReport(req)
      .then((res) => {
        setComparisonReport(res?.data);
      })
      .catch((err) => {
        setComparisonReport(err.message);
      })
      .finally(() => setIsComparisonLoading(false));
  }, [comparedTo, currentCampaignId, surveyResponseId]);

  const onExportPDF = () => {
    setIsPrintAsPDFLoading(true);
    const req: GetReportRequest = {
      surveyCampaignId: getQueryParam("surveyCampaignId") || "",
      format: "pdf",
      surveyResponseId: surveyResponseId,
      layout: GetReportRequestLayout.Individual,
      comparedTo: comparedTo
    };
    getReport(req)
      .catch((err) => {
        message.error(err);
      })
      .finally(() => setIsPrintAsPDFLoading(false));
  };

  if (isError) {
    return (
      <div>
        <h1>{t(FeedbackGroupQuestionReportKeys.feedbackReport_oopsInvalidSurveyCampaign)}</h1>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingOutlined style={{ fontSize: 50 }} />;
  }

  const columns = [
    {
      title: (
        <>
          {t(
            FeedbackGroupQuestionReportKeys.feedbackIndividualView_nameTableHeader
          )}
        </>
      ),
      width: 200,
      render: (rowData: SurveyResponse) => {
        return (
          <span>
            {rowData.surveyEmployee.fname} {rowData.surveyEmployee.lname} ({rowData.surveyEmployee.team})
          </span>
        );
      },
      sorter: (a: SurveyResponse, b: SurveyResponse) =>
        a.surveyEmployee.email.localeCompare(b.surveyEmployee.email),
    },
    {
      title: (
        <>
          {t(
            FeedbackGroupQuestionReportKeys.feedbackIndividualView_actionsTableHeader
          )}
        </>
      ),
      render: (rowData: SurveyResponse) => {
        const isViewing = rowData._id === surveyResponseId
        return (
          <div
            style={{
              display: "flex",
              gap: 5,
            }}
          >
            <Button
              type="link"
              disabled={isViewing}
              onMouseEnter={() => {
                // warm up the network cache
                const req: GetReportRequest = {
                  surveyCampaignId: getQueryParam("surveyCampaignId") || "",
                  format: "html",
                  surveyResponseId: rowData._id,
                  layout: GetReportRequestLayout.Individual,
                };
                getReport(req)

                // warm up network cache
                getSurveyCampaigns({
                  surveyTemplateId: props.report?.surveyCampaign?.surveyTemplateId,
                  createdBefore: props.report?.surveyCampaign?.createdAt,
                  email: rowData.surveyEmployee.email,
                })
              }}
              onClick={() => {
                setQueryParam("individual.comparedTo", "")
                setQueryParam("individual.chooseOrView", "")
                setQueryParam("surveyResponseId", rowData._id);
                setComparedTo("")
                setSurveyResponseId(rowData._id);
              }}
            >
              {t(FeedbackGroupQuestionReportKeys.feedbackIndividualView_viewReportLink)}
            </Button>
          </div>
        );
      },
    },
  ];

  const participants: SurveyResponse[] =
    getSurveyCampaignBasicInfoResponse?.surveyResponsesByParticipant?.map(
      (sr) => sr.participant
    ) || [];

  const selectedEmployee = participants.find(p => p._id === surveyResponseId);
  const employee = selectedEmployee?.surveyEmployee

  const isEnableBehavioralChangeFeature = context?.featureFlags?.find((ff) => {
    return (
      ff.key === FeatureFlagTemplateKey.ENABLE_BEHAVIORAL_CHANGE &&
      ff.status === FeatureFlagStatus.ENABLED
    );
  })
    ? true
    : false;

  if (isEnableBehavioralChangeFeature) {
    return (
      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "flex-start",
          flexDirection: "row",
        }}
      >
        <Card
          style={{
            width: "30%",
            minWidth: 500,
            overflow: "scroll",
          }}
        >
          <Table
            rowKey="_id"
            columns={columns}
            dataSource={participants}
            expandable={{
              expandedRowRender: (participant: SurveyResponse) => {
                const surveyResponses =
                  getSurveyCampaignBasicInfoResponse?.surveyResponsesByParticipant?.find(
                    (sr) => {
                      return sr.participant._id === participant._id;
                    }
                  )?.surveyResponses || [];
                const peers = surveyResponses?.filter((d) => {
                  const isPeer =
                    d.surveyEmployee.participantId ===
                    participant.surveyEmployee._id;

                  return isPeer;
                });

                const columns = [
                  {
                    title: (
                      <>
                        {t(
                          FeedbackGroupQuestionReportKeys.feedbackIndividualView_emailTableHeader
                        )}
                      </>
                    ),
                    width: 200,
                    render: (rowData: SurveyResponse) => {
                      return (
                        <span>
                          {rowData.surveyEmployee.email} (
                          {rowData.surveyEmployee.team})
                        </span>
                      );
                    },
                    sorter: (a: SurveyResponse, b: SurveyResponse) =>
                      a.surveyEmployee.email.localeCompare(
                        b.surveyEmployee.email
                      ),
                  },
                  {
                    title: t(FeedbackGroupQuestionReportKeys.feedbackReport_statusTableHeader),
                    render: (rowData: SurveyResponse) => {
                      return <SurveyResponseStatusTag status={rowData.status} />;
                    },
                    sorter: (a: SurveyResponse, b: SurveyResponse) =>
                      a.status.localeCompare(b.status),
                  },
                  {
                    title: (
                      <>
                        {t(
                          FeedbackGroupQuestionReportKeys.feedbackIndividualView_positionTableHeader
                        )}
                      </>
                    ),
                    render: (rowData: SurveyResponse) => {
                      return (
                        <PositionsTag
                          position={rowData.surveyEmployee.position}
                        />
                      );
                    },
                    sortDirection: "ascend",
                    sorter: (a: SurveyResponse, b: SurveyResponse) =>
                      a.status.localeCompare(b.status),
                  },
                  {
                    title: (
                      <>
                        {t(
                          FeedbackGroupQuestionReportKeys.feedbackIndividualView_lastUpdatedTableHeader
                        )}
                      </>
                    ),
                    width: 200,
                    dataIndex: "updatedAt",
                    render: (time) => parseTime(time),
                  },
                ];

                return (
                  <Table
                    key={participant._id}
                    rowKey="_id"
                    columns={columns}
                    dataSource={peers}
                  />
                );
              },
            }}
          />
        </Card>
        <Card
          style={{
            width: "70%",
          }}
        >
          {surveyResponseId ? (
            <Tabs
              activeKey={currentOrCompare}
              onChange={key => {
                setCurrentOrCompare(key as CurrentOrCompare)
                setQueryParam('individual.currentOrCompare', key)
              }}
            >
              <Tabs.TabPane tab={t(FeedbackGroupQuestionReportKeys.feedbackReport_currentReportTab)} key="current">
                <Fragment>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      width: "100%",
                      gap: 20,
                      marginBottom: 50,
                    }}
                  >
                    {isPrintAsPDFLoading ? (
                      <LoadingOutlined style={{ fontSize: 24 }} />
                    ) : (
                      <Tooltip
                        title={t(
                          FeedbackGroupQuestionReportKeys.feedbackGroupQuestionReport_downloadAsPDFTooltip
                        )}
                      >
                        <FilePdfFilled
                          data-testid={
                            TEST_IDS.REPORT_PAGE.individual_download_as_pdf_button_id
                          }
                          onClick={(e) => {
                            onExportPDF();
                          }}
                          style={{ fontSize: 24 }}
                        />
                      </Tooltip>
                    )}
                  </div>

                  <div
                    dangerouslySetInnerHTML={{
                      __html: report,
                    }}
                  ></div>
                </Fragment>
              </Tabs.TabPane>
              <Tabs.TabPane
                tab={
                  <span>
                    {t(FeedbackGroupQuestionReportKeys.feedbackReport_behaviouralChangeReportTab)}
                    <Tag color="green" style={{ marginLeft: 4 }}>
                      {t(FeedbackGroupQuestionReportKeys.feedbackReport_newTag)}
                    </Tag>
                  </span>
                }
                key="compare"
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {/* Past vs Present slots - shared between both views */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 40px 1fr", gap: 8, alignItems: "center" }}>
                    <ComparisonSlot
                      pastOrPresent="past"
                      surveyCampaign={getSurveyCampaignsResponse?.surveyCampaigns.find(sc => sc._id === comparedTo)}
                      employee={employee}
                      showClear={chooseOrView === "choose"}
                      onClear={() => {
                        setComparedTo(undefined);
                        setQueryParam("individual.comparedTo", "");
                      }}
                    />
                    <div style={{ textAlign: "center", fontSize: 12, color: "#bfbfbf", fontWeight: 500 }}>{t(FeedbackGroupQuestionReportKeys.feedbackReport_vsLabel)}</div>
                    <ComparisonSlot
                      pastOrPresent="present"
                      surveyCampaign={props.report?.surveyCampaign}
                      employee={employee}
                    />
                  </div>

                  {chooseOrView === "view" ? (
                    // Results view
                    <>
                      {/* Action bar */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: "8px 0",
                        borderBottom: "1px solid #f0f0f0",
                      }}>
                        <Button
                          type="link"
                          icon={<ArrowLeftOutlined />}
                          onClick={() => {
                            setChooseOrView("choose");
                            setQueryParam("individual.chooseOrView", "choose");
                          }}
                        >
                          {t(FeedbackGroupQuestionReportKeys.feedbackReport_changeSelectionButton)}
                        </Button>
                        {isPrintAsPDFLoading ? (
                          <LoadingOutlined style={{ fontSize: 24 }} />
                        ) : (
                          <Tooltip title={t(FeedbackGroupQuestionReportKeys.feedbackGroupQuestionReport_downloadAsPDFTooltip)}>
                            <FilePdfFilled
                              data-testid={TEST_IDS.REPORT_PAGE.individual_download_as_pdf_button_id}
                              onClick={() => onExportPDF()}
                              style={{ fontSize: 24, cursor: "pointer" }}
                            />
                          </Tooltip>
                        )}
                      </div>

                      {isComparisonLoading ? (
                        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
                          <LoadingOutlined spin={true} style={{ fontSize: 32, color: COLORS.PRIMARY_COLOR }} />
                        </div>
                      ) : (
                        <div dangerouslySetInnerHTML={{ __html: comparisonReport }}></div>
                      )}
                    </>
                  ) : (
                    // Picker view
                    <>
                      {/* Campaign list */}
                      <List
                          dataSource={getSurveyCampaignsResponse?.surveyCampaigns.filter((sc) => sc._id !== currentCampaignId)}
                        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t(FeedbackGroupQuestionReportKeys.feedbackReport_noPreviousCampaignsFound)} /> }}
                        renderItem={(c) => (
                          <List.Item
                            data-testclass='compareto-survey-campaigns-list'
                            key={c._id}
                            onClick={() => {
                              setQueryParam("individual.comparedTo", c._id);
                              setComparedTo(c._id); // Preload the comparison report
                            }}
                            style={{
                              cursor: "pointer",
                              background: comparedTo === c._id ? COMPARISON_COLORS.past.background : undefined,
                              paddingLeft: 16,
                              paddingRight: 16,
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span>{c.name}</span>
                              </div>
                              <p style={{ color: "grey" }}>{parseDate(c.createdAt)}</p>
                            </div>
                          </List.Item>
                        )}
                      />

                      {/* Compare button */}
                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                          type="primary"
                          disabled={!comparedTo}
                          onClick={() => {
                            setQueryParam("individual.chooseOrView", "view");
                            setChooseOrView("view")
                          }}
                        >
                          {t(FeedbackGroupQuestionReportKeys.feedbackReport_compareButton)}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </Tabs.TabPane>
            </Tabs>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t(FeedbackGroupQuestionReportKeys.feedbackReport_selectTeamFromLeft)}
            />
          )}
        </Card>
      </div>
    );
  } else {
    return (
      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "flex-start",
          flexDirection: "row",
        }}
      >
        <Card
          style={{
            width: "30%",
            minWidth: 500,
            overflow: "scroll",
          }}
        >
          <Table
            rowKey="_id"
            columns={columns}
            dataSource={participants}
            expandable={{
              expandedRowRender: (participant: SurveyResponse) => {
                const surveyResponses =
                  getSurveyCampaignBasicInfoResponse?.surveyResponsesByParticipant?.find(
                    (sr) => {
                      return sr.participant._id === participant._id;
                    }
                  )?.surveyResponses || [];
                const peers = surveyResponses?.filter((d) => {
                  const isPeer =
                    d.surveyEmployee.participantId ===
                    participant.surveyEmployee._id;

                  return isPeer;
                });

                const columns = [
                  {
                    title: (
                      <>
                        {t(
                          FeedbackGroupQuestionReportKeys.feedbackIndividualView_emailTableHeader
                        )}
                      </>
                    ),
                    width: 200,
                    render: (rowData: SurveyResponse) => {
                      return (
                        <span>
                          {rowData.surveyEmployee.email} (
                          {rowData.surveyEmployee.team})
                        </span>
                      );
                    },
                    sorter: (a: SurveyResponse, b: SurveyResponse) =>
                      a.surveyEmployee.email.localeCompare(
                        b.surveyEmployee.email
                      ),
                  },
                  {
                    title: t(FeedbackGroupQuestionReportKeys.feedbackReport_statusTableHeader),
                    render: (rowData: SurveyResponse) => {
                      return <SurveyResponseStatusTag status={rowData.status} />;
                    },
                    sorter: (a: SurveyResponse, b: SurveyResponse) =>
                      a.status.localeCompare(b.status),
                  },
                  {
                    title: (
                      <>
                        {t(
                          FeedbackGroupQuestionReportKeys.feedbackIndividualView_positionTableHeader
                        )}
                      </>
                    ),
                    render: (rowData: SurveyResponse) => {
                      return (
                        <PositionsTag
                          position={rowData.surveyEmployee.position}
                        />
                      );
                    },
                    sortDirection: "ascend",
                    sorter: (a: SurveyResponse, b: SurveyResponse) =>
                      a.status.localeCompare(b.status),
                  },
                  {
                    title: (
                      <>
                        {t(
                          FeedbackGroupQuestionReportKeys.feedbackIndividualView_lastUpdatedTableHeader
                        )}
                      </>
                    ),
                    width: 200,
                    dataIndex: "updatedAt",
                    render: (time) => parseTime(time),
                  },
                ];

                return (
                  <Table
                    key={participant._id}
                    rowKey="_id"
                    columns={columns}
                    dataSource={peers}
                  />
                );
              },
            }}
          />
        </Card>
        <Card
          style={{
            width: "70%",
          }}
        >
          {surveyResponseId ? (
            <Fragment>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                  gap: 20,
                  marginBottom: 50,
                }}
              >
                {isPrintAsPDFLoading ? (
                  <LoadingOutlined style={{ fontSize: 30 }} />
                ) : (
                  <Tooltip
                    title={t(
                      FeedbackGroupQuestionReportKeys.feedbackGroupQuestionReport_downloadAsPDFTooltip
                    )}
                  >
                    <FilePdfFilled
                      data-testid={
                        TEST_IDS.REPORT_PAGE.individual_download_as_pdf_button_id
                      }
                      onClick={(e) => {
                        onExportPDF();
                      }}
                      style={{ fontSize: 30 }}
                    />
                  </Tooltip>
                )}
              </div>

              <div
                dangerouslySetInnerHTML={{
                  __html: report,
                }}
              ></div>
            </Fragment>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t(FeedbackGroupQuestionReportKeys.feedbackReport_selectTeamFromLeft)}
            />
          )}
        </Card>
      </div>
    );
  }
}
